import { Button } from '@/ui/Button';
import { InputSuelto, SelectSuelto } from '@/ui/Field';
import type { Campo, ReglaDeIndicador } from './api';
import { claveDe } from './reglas';

/**
 * camposInteligentes.tsx — la configuración de los nueve tipos que se calculan
 * solos: tiempo transcurrido, vencimiento, estado, calculado, fórmula,
 * relación, indicador, progreso y vacaciones.
 *
 * Es la parte del constructor que más se mira y la que menos se toca: el
 * vanilla la escribía como HTML en línea con `onchange` a funciones globales, y
 * aquí es el mismo formulario con controles de verdad. Lo que **no** cambia es
 * qué guarda cada tipo en `config`: eso lo lee `smart_fields.py`, y cambiarlo
 * rompería las columnas ya configuradas.
 */

type Valor = string | number | ReglaDeIndicador[] | undefined;

export interface PropsDeConfig {
  campo: Campo;
  /** Los demás campos del recurso, para las referencias por clave. */
  otros: Campo[];
  /** Solo lo que necesita el selector de «Relación». */
  recursos: Array<{ id: number; name: string }>;
  onCambio: (clave: string, valor: Valor) => void;
}

function leer(campo: Campo, clave: string): string {
  const v = campo.config?.[clave];
  return v == null || Array.isArray(v) ? '' : String(v);
}

/** Un desplegable con las claves de los demás campos. */
function SelectorDeCampo({
  otros, valor, onCambio, conHoy, etiqueta,
}: {
  otros: Campo[];
  valor: string;
  onCambio: (v: string) => void;
  conHoy?: boolean;
  etiqueta: string;
}) {
  return (
    <SelectSuelto
      aria-label={etiqueta}
      className="w-auto min-w-[8rem] py-1 text-2"
      value={valor}
      onChange={(e) => onCambio(e.target.value)}
    >
      <option value="">— campo —</option>
      {conHoy && <option value="today">Hoy</option>}
      {otros.map((f) => (
        <option key={claveDe(f)} value={claveDe(f)}>{f.label || claveDe(f)}</option>
      ))}
    </SelectSuelto>
  );
}

/** Un campo o un número fijo, que es lo que admiten «calculado» y «progreso». */
function CampoONumero({
  otros, valor, onCambio, etiqueta,
}: {
  otros: Campo[];
  valor: string;
  onCambio: (v: string) => void;
  etiqueta: string;
}) {
  const esNumero = valor !== '' && !otros.some((f) => claveDe(f) === valor) && !Number.isNaN(Number(valor));
  return (
    <>
      <SelectSuelto
        aria-label={etiqueta}
        className="w-auto min-w-[8rem] py-1 text-2"
        value={esNumero ? '__num__' : valor}
        onChange={(e) => onCambio(e.target.value === '__num__' ? '0' : e.target.value)}
      >
        <option value="">— campo —</option>
        {otros.map((f) => (
          <option key={claveDe(f)} value={claveDe(f)}>{f.label || claveDe(f)}</option>
        ))}
        <option value="__num__">número fijo…</option>
      </SelectSuelto>
      {esNumero && (
        <InputSuelto
          type="number"
          aria-label={`${etiqueta}: número`}
          className="w-20 py-1 text-2"
          value={valor}
          onChange={(e) => onCambio(e.target.value)}
        />
      )}
    </>
  );
}

const Fila = ({ children }: { children: React.ReactNode }) => (
  <div className="flex flex-wrap items-center gap-2">{children}</div>
);

const Etiqueta = ({ children }: { children: React.ReactNode }) => (
  <span className="text-1 text-muted">{children}</span>
);

export function ConfigDeCampoInteligente(props: PropsDeConfig) {
  const { campo, otros, recursos, onCambio } = props;
  const c = (k: string) => leer(campo, k);

  switch (campo.type) {
    case 'tiempo_transcurrido':
      return (
        <Fila>
          <Etiqueta>Desde</Etiqueta>
          <SelectorDeCampo etiqueta="Campo de inicio" otros={otros} valor={c('source')}
            onCambio={(v) => onCambio('source', v)} />
          <Etiqueta>hasta</Etiqueta>
          <SelectorDeCampo etiqueta="Campo de fin" otros={otros} valor={c('to') || 'today'} conHoy
            onCambio={(v) => onCambio('to', v)} />
          <Etiqueta>formato</Etiqueta>
          <SelectSuelto
            aria-label="Formato" className="w-auto py-1 text-2"
            value={c('format') || 'ymd'} onChange={(e) => onCambio('format', e.target.value)}
          >
            <option value="ymd">Años·meses·días</option>
            <option value="years">Años</option>
            <option value="months">Meses</option>
            <option value="days">Días</option>
          </SelectSuelto>
        </Fila>
      );

    case 'vencimiento':
      return (
        <Fila>
          <Etiqueta>Fecha base</Etiqueta>
          <SelectorDeCampo etiqueta="Fecha base" otros={otros} valor={c('source')}
            onCambio={(v) => onCambio('source', v)} />
          <Etiqueta>+ vigencia</Etiqueta>
          <InputSuelto
            type="number" aria-label="Vigencia" className="w-20 py-1 text-2"
            value={c('vigencia_num')} onChange={(e) => onCambio('vigencia_num', e.target.value)}
          />
          <SelectSuelto
            aria-label="Unidad de la vigencia" className="w-auto py-1 text-2"
            value={c('vigencia_unit') || 'months'}
            onChange={(e) => onCambio('vigencia_unit', e.target.value)}
          >
            <option value="days">días</option>
            <option value="months">meses</option>
            <option value="years">años</option>
          </SelectSuelto>
        </Fila>
      );

    case 'estado':
      return (
        <Fila>
          <Etiqueta>Fecha objetivo</Etiqueta>
          <SelectorDeCampo etiqueta="Fecha objetivo" otros={otros} valor={c('source')}
            onCambio={(v) => onCambio('source', v)} />
          <Etiqueta>avisar</Etiqueta>
          <InputSuelto
            type="number" aria-label="Días de aviso" className="w-20 py-1 text-2"
            value={c('warn_days') || '30'} onChange={(e) => onCambio('warn_days', e.target.value)}
          />
          <Etiqueta>días antes</Etiqueta>
        </Fila>
      );

    case 'calculado':
      return (
        <Fila>
          <CampoONumero etiqueta="Primer operando" otros={otros} valor={c('a')}
            onCambio={(v) => onCambio('a', v)} />
          <SelectSuelto
            aria-label="Operación" className="w-auto py-1 text-2"
            value={c('op') || 'subtract'} onChange={(e) => onCambio('op', e.target.value)}
          >
            <option value="subtract">−</option>
            <option value="sum">+</option>
            <option value="multiply">×</option>
            <option value="divide">÷</option>
            <option value="datediff">dif. fechas</option>
          </SelectSuelto>
          <CampoONumero etiqueta="Segundo operando" otros={otros} valor={c('b')}
            onCambio={(v) => onCambio('b', v)} />
          {c('op') === 'datediff' ? (
            <SelectSuelto
              aria-label="Unidad" className="w-auto py-1 text-2"
              value={c('unit') || 'years'} onChange={(e) => onCambio('unit', e.target.value)}
            >
              <option value="years">años</option>
              <option value="months">meses</option>
              <option value="days">días</option>
            </SelectSuelto>
          ) : (
            <>
              <Etiqueta>redondeo</Etiqueta>
              <InputSuelto
                type="number" aria-label="Redondeo" className="w-16 py-1 text-2"
                value={c('round')} onChange={(e) => onCambio('round', e.target.value)}
              />
            </>
          )}
        </Fila>
      );

    case 'formula':
      return (
        <div>
          <InputSuelto
            aria-label="Fórmula"
            className="w-full py-1 font-mono text-2"
            value={c('expr')}
            onChange={(e) => onCambio('expr', e.target.value)}
            placeholder='DATEDIFF([ingreso], TODAY(), "years")'
          />
          <p className="mt-1 text-1 text-muted">
            Funciones: TODAY() DATEDIFF() IF() SUM() ROUND() CONCAT() UPPER() LOWER() YEAR() ·
            referencia campos con [clave]. Claves:{' '}
            {otros.length ? otros.map((f) => claveDe(f)).join(', ') : '—'}
          </p>
        </div>
      );

    case 'relacion':
      return (
        <Fila>
          <Etiqueta>Recurso</Etiqueta>
          <SelectSuelto
            aria-label="Recurso relacionado" className="w-auto py-1 text-2"
            value={c('target_resource_id')}
            onChange={(e) => onCambio('target_resource_id', e.target.value)}
          >
            <option value="">— recurso —</option>
            {recursos.map((r) => (
              <option key={r.id} value={String(r.id)}>{r.name}</option>
            ))}
          </SelectSuelto>
          <Etiqueta>donde su</Etiqueta>
          <InputSuelto
            aria-label="Campo del recurso destino" className="w-28 py-1 text-2"
            value={c('match_field')} onChange={(e) => onCambio('match_field', e.target.value)}
            placeholder="campo destino"
          />
          <Etiqueta>=</Etiqueta>
          <SelectorDeCampo etiqueta="Campo de este recurso" otros={otros} valor={c('this_field')}
            onCambio={(v) => onCambio('this_field', v)} />
          <SelectSuelto
            aria-label="Qué hacer con lo encontrado" className="w-auto py-1 text-2"
            value={c('agg') || 'count'} onChange={(e) => onCambio('agg', e.target.value)}
          >
            <option value="count">Contar</option>
            <option value="list">Listar</option>
            <option value="sum">Sumar</option>
          </SelectSuelto>
          {(c('agg') === 'list' || c('agg') === 'sum') && (
            <InputSuelto
              aria-label="Campo a listar o sumar" className="w-28 py-1 text-2"
              value={c('agg_field')} onChange={(e) => onCambio('agg_field', e.target.value)}
              placeholder="campo destino"
            />
          )}
        </Fila>
      );

    case 'progreso':
      return (
        <Fila>
          <Etiqueta>Valor</Etiqueta>
          <CampoONumero etiqueta="Valor" otros={otros} valor={c('value')}
            onCambio={(v) => onCambio('value', v)} />
          <Etiqueta>de máx</Etiqueta>
          <CampoONumero etiqueta="Máximo" otros={otros} valor={c('max')}
            onCambio={(v) => onCambio('max', v)} />
        </Fila>
      );

    case 'vacaciones':
      return (
        <Fila>
          <Etiqueta>Fecha de ingreso</Etiqueta>
          <SelectorDeCampo etiqueta="Fecha de ingreso" otros={otros} valor={c('source')}
            onCambio={(v) => onCambio('source', v)} />
          <Etiqueta>tasa</Etiqueta>
          <InputSuelto
            type="number" aria-label="Días por año" className="w-16 py-1 text-2"
            value={c('rate') || '15'} onChange={(e) => onCambio('rate', e.target.value)}
          />
          <Etiqueta>días/año</Etiqueta>
          <SelectSuelto
            aria-label="Qué se cuenta" className="w-auto py-1 text-2"
            value={c('mode') || 'generadas'} onChange={(e) => onCambio('mode', e.target.value)}
          >
            <option value="generadas">Generadas</option>
            <option value="pendientes">Pendientes</option>
          </SelectSuelto>
          {c('mode') === 'pendientes' && (
            <>
              <Etiqueta>− tomadas</Etiqueta>
              <SelectorDeCampo etiqueta="Días tomados" otros={otros} valor={c('taken')}
                onCambio={(v) => onCambio('taken', v)} />
            </>
          )}
        </Fila>
      );

    case 'indicador':
      return <Indicador {...props} />;

    default:
      return null;
  }
}

/** El indicador es el único con una lista de reglas dentro. */
function Indicador({ campo, otros, onCambio }: PropsDeConfig) {
  const reglas = (campo.config?.rules as ReglaDeIndicador[] | undefined) ?? [];
  const cambiar = (i: number, clave: keyof ReglaDeIndicador, valor: string) =>
    onCambio('rules', reglas.map((r, j) => (j === i ? { ...r, [clave]: valor } : r)));

  return (
    <div>
      <p className="mb-1 flex flex-wrap items-center gap-2 text-1 text-muted">
        Según
        <SelectorDeCampo
          etiqueta="Campo que se mira" otros={otros}
          valor={campo.config?.source == null ? '' : String(campo.config.source)}
          onCambio={(v) => onCambio('source', v)}
        />
        mostrar:
      </p>
      <ul className="flex flex-col gap-1">
        {reglas.map((r, i) => (
          <li key={i} className="flex flex-wrap items-center gap-1.5">
            <SelectSuelto
              aria-label={`Comparación ${i + 1}`} className="w-auto py-1 text-2"
              value={r.op} onChange={(e) => cambiar(i, 'op', e.target.value)}
            >
              {['<', '<=', '>', '>=', '=', '!='].map((o) => <option key={o}>{o}</option>)}
            </SelectSuelto>
            <InputSuelto
              aria-label={`Valor ${i + 1}`} className="w-20 py-1 text-2"
              value={r.value ?? ''} onChange={(e) => cambiar(i, 'value', e.target.value)}
              placeholder="valor"
            />
            <span className="text-1" aria-hidden="true">→</span>
            <InputSuelto
              aria-label={`Etiqueta ${i + 1}`} className="min-w-[8rem] flex-1 py-1 text-2"
              value={r.label ?? ''} onChange={(e) => cambiar(i, 'label', e.target.value)}
              placeholder="etiqueta (ej: Alto)"
            />
            <Button
              variant="ghost" size="sm" iconOnly
              aria-label={`Quitar la regla ${i + 1}`}
              onClick={() => onCambio('rules', reglas.filter((_, j) => j !== i))}
            >
              <i className="bi bi-x-lg text-danger-ink" aria-hidden="true" />
            </Button>
          </li>
        ))}
      </ul>
      <Button
        variant="secondary" size="sm" className="mt-1"
        onClick={() => onCambio('rules', [...reglas, { op: '>=', value: '', label: '' }])}
      >
        <i className="bi bi-plus" aria-hidden="true" /> Regla
      </Button>
    </div>
  );
}
